// src/components/ProcessSection.jsx
import React from 'react';
import {
  Box,
  Container,
  Typography,
  useTheme,
  useMediaQuery,
  styled,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaSearch,
  FaDraftingCompass,
  FaCode,
  FaRocket,
} from 'react-icons/fa';

// ─── STEPS DATA ─────────────────────────────────────────────────────────────
const steps = [
  { title: 'Discover', desc: 'Define goals, audience & strategy.', Icon: FaSearch },
  { title: 'Design',   desc: 'Wireframes & UI aligned to brand.', Icon: FaDraftingCompass },
  { title: 'Develop',  desc: 'Clean, scalable code & best practices.', Icon: FaCode },
  { title: 'Deploy',   desc: 'CI/CD pipelines & 24/7 support.', Icon: FaRocket },
];

// ─── STYLED COMPONENTS ──────────────────────────────────────────────────────
const StepCircle = styled(motion.div)(({ theme }) => ({
  width: 64,
  height: 64,
  borderRadius: '50%',
  backgroundColor: theme.palette.info.main,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  flexShrink: 0,
}));

const Connector = styled(motion.div)(({ theme, vertical }) => ({
  flex: 1,
  backgroundColor: theme.palette.info.main,
  ...(vertical
    ? { width: 2, height: 40, margin: '8px 0' }
    : { height: 2, width: 40, margin: '0 8px' }),
}));

// ─── ANIMATION VARIANTS ────────────────────────────────────────────────────
const containerVariants = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.3 },
  },
};

const circleVariants = {
  hidden: { scale: 0, opacity: 0 },
  show:   { scale: 1, opacity: 1, transition: { type: 'spring', stiffness: 200 } },
};

const connectorVariants = {
  hidden: { scale: 0 },
  show:   { scale: 1, transition: { duration: 0.5, ease: 'easeInOut' } },
};

// ─── MAIN COMPONENT ────────────────────────────────────────────────────────
export default function ProcessSection() {
  const theme = useTheme();
  const mdUp  = useMediaQuery(theme.breakpoints.up('md'));

  return (
    <Box
      component="section"
      id="process"
      sx={{
        bgcolor: theme.palette.background.paper,
        py: { xs: 6, md: 10 },
      }}
    >
      <Container maxWidth="lg">
        <Typography
          variant="h4"
          align="center"
          fontWeight={700}
          sx={{ mb: { xs: 4, md: 6 }, color: theme.palette.primary.main }}
        >
          Our Process
        </Typography>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-100px' }}
          style={{
            display: 'flex',
            flexDirection: mdUp ? 'row' : 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {steps.map((step, i) => (
            <React.Fragment key={step.title}>
              {/* Circle */}
              <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <StepCircle variants={circleVariants}>
                  <step.Icon size={28} />
                </StepCircle>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 2 }}>
                  {step.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 160 }}>
                  {step.desc}
                </Typography>
              </Box>

              {/* Connector (skip after last) */}
              {i < steps.length - 1 && (
                <Connector
                  variants={connectorVariants}
                  vertical={!mdUp}
                  style={{ transformOrigin: 'center' }}
                />
              )}
            </React.Fragment>
          ))}
        </motion.div>
      </Container>
    </Box>
  );
}
